import cn from 'classnames';
import type { FC } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormSpy, useField, useForm } from 'react-final-form';
import { Spinner } from '@alfa-bank/site-spinner';
import { NamedContainer } from '@alfa-bank/site-named-container';
import { InformationCircleMIcon } from '@alfalab/icons-glyph/InformationCircleMIcon';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';
import { useNamedContainersWithChildren } from '@alfa-bank/site-named-container/utils';
import {
    updateClientAddress,
    selectAppData,
    selectDeliveryData,
    selectFirstStepData,
} from '@alfa-bank/site-dc-form-api/store';
import { checkChildBrokerage } from '@alfa-bank/site-dc-form-api/src/utils/others';

import { deliveryStepAnalytics as analytics } from '../../../../analytics';
import type stylesDesktop from '../../desktop/styles.css';
import { CodeWordField } from '../code-word-field';
import { ADDRESS_DATA_NAME, ADDRESS_NAME } from '../../config';
import type { IDeliveryStepFormValues } from '../../models';
import { FormSpyLocal } from '../form-spy';

import type { TDeliveryStepForm } from './models';

export const DeliveryStepForm: FC<TDeliveryStepForm> = ({
    AddressField,
    BrokeragePlate,
    Button,
    DeliveryForm,
    NoDeliveryPlate,
    Plate,
    ProgressBar,
    Text,
    address,
    alertText,
    brokerPlate,
    buttonText,
    children,
    clientPlate,
    codeWord,
    comment,
    config,
    courierForm,
    isFirstTimeSlotPreSelected,
    noDeliveryAlertText,
    progressBar,
    progressType,
    selectedOffice,
    selectedTime,
    styles,
    title,
}) => {
    const formApi = useForm<IDeliveryStepFormValues>();
    const dispatch = useDispatch();
    const { productsSlotSecondStep } = useNamedContainersWithChildren(children);
    const { input } = useField('deliveryType');
    const deliveryValue: 'courier' | 'offices' | undefined = input.value;
    const { availableDeliveries, deliveryScreenStatus, officeDeliveryType } =
        useSelector(selectDeliveryData);
    const { requestBrokerageAccount, requestCodeWord, userStatus } = useSelector(selectAppData);
    const { cashbackApplyStatus, packageCashback, passportBirthDate } =
        useSelector(selectFirstStepData);

    const [noDeliveryPlate, showNoDeliveryPlate] = useState<boolean>(false);
    const isLoading = deliveryScreenStatus === 'deliveriesLoading';
    const isApiLoading = deliveryScreenStatus === 'finishLoading';
    const showCashbackError =
        packageCashback && (cashbackApplyStatus === 'ERROR' || userStatus?.includes('NODT_ALL_CW'));

    const handleAddressSelect = () => {
        const addressState = formApi.getFieldState(ADDRESS_NAME);
        const addressData = formApi.getState().values[ADDRESS_DATA_NAME];

        if (addressState?.valid && addressData) {
            dispatch(updateClientAddress(addressData));
        }
    };

    useEffect(() => {
        if (showCashbackError) {
            sendEvent(analytics.cashbackError);
        }
    }, [showCashbackError]);

    const { isButtonOutSide, marginsPreset, textPreset } = config;

    const submitButton = useMemo(
        () => (
            <Button
                block
                borderRadius={8}
                className={styles?.submitButton ? cn(styles.submitButton, styles.external) : ''}
                dataTestId="submit-button"
                hasAnimation
                loading={isApiLoading && Boolean(availableDeliveries)}
                onClick={() => {
                    if (!availableDeliveries) {
                        showNoDeliveryPlate(true);
                    }
                }}
                size="s"
                styler={marginsPreset.buttonMargin}
                type="submit"
                view="primary"
            >
                {buttonText}
            </Button>
        ),
        [
            availableDeliveries,
            buttonText,
            Button,
            isApiLoading,
            marginsPreset.buttonMargin,
            styles.external,
            styles.submitButton,
        ]
    );

    const geBrokeragePlate = () => {
        if (!brokerPlate || !requestBrokerageAccount) return;

        const isUserChild = checkChildBrokerage({
            passportBirthDate,
            minAge: brokerPlate?.child?.ages?.minChildAge,
            maxAge: brokerPlate?.child?.ages?.maxChildAge,
        });

        if (brokerPlate?.child && isUserChild) {
            // eslint-disable-next-line react/jsx-props-no-spreading
            return <BrokeragePlate {...brokerPlate.child} />;
        }
        if (brokerPlate?.adult && !isUserChild) {
            // eslint-disable-next-line react/jsx-props-no-spreading
            return <BrokeragePlate {...brokerPlate.adult} />;
        }
    };

    return (
        <>
            <FormSpy
                component={FormSpyLocal}
                subscription={{
                    values: true,
                    touched: true,
                    active: true,
                    errors: true,
                }}
            />
            {title && (
                <Text
                    className={cn((styles as typeof stylesDesktop).title, styles.external)}
                    styler={marginsPreset.titleMargin}
                    tag="h3"
                    view={textPreset.title}
                >
                    {title}
                </Text>
            )}
            <div className={cn(styles.flexibleDesignDelivery, styles.external)}>
                {progressBar && (
                    <>
                        <Text styler={{ marginTop: 0, marginBottom: 8 }} view="primary-small">
                            {progressBar.text}
                        </Text>
                        <ProgressBar
                            {...progressBar}
                            topTextMask={
                                deliveryValue
                                    ? progressBar?.deliveryText[deliveryValue]
                                    : progressBar.topTextMask
                            }
                            type={progressType}
                        />
                    </>
                )}
                <AddressField {...address} name={ADDRESS_NAME} onSelect={handleAddressSelect} />
                {showCashbackError && (
                    <Plate
                        border
                        className={cn(styles?.clientPlate, styles?.external)}
                        dataTestId="dc-form-plate-cashback-not-available"
                        styler={{ marginTop: 24 }}
                        view="attention"
                    >
                        <NamedContainer name="leftAddons">
                            <InformationCircleMIcon
                                className={cn(styles?.iconHint, styles?.external)}
                            />
                        </NamedContainer>
                        {clientPlate}
                    </Plate>
                )}
                {isLoading && (
                    <Text styler={marginsPreset.loaderMargin} tag="div" view="primary-medium">
                        <Spinner styler={{ marginRight: 8 }} />
                        Подбираем варианты доставки
                    </Text>
                )}
                {!isLoading && availableDeliveries !== undefined && (
                    <DeliveryForm
                        alertText={alertText}
                        comment={comment}
                        courierFormSettings={courierForm}
                        isFirstTimeSlotPreSelected={isFirstTimeSlotPreSelected}
                        officeDeliveryType={officeDeliveryType}
                        offices={availableDeliveries?.offices}
                        selectedOffice={selectedOffice}
                        selectedTime={selectedTime}
                        styler={marginsPreset.deliveryFormMargin}
                        timeSlots={availableDeliveries?.timeSlots}
                    />
                )}
                {requestCodeWord && (
                    <>
                        <Text
                            styler={marginsPreset.codeTitleMargin}
                            tag="h4"
                            view={textPreset.codeTitle}
                        >
                            {codeWord.title}
                        </Text>

                        <CodeWordField {...codeWord} />
                    </>
                )}

                {geBrokeragePlate()}

                {!isButtonOutSide && submitButton}
                <NoDeliveryPlate
                    isShown={noDeliveryPlate}
                    noDeliveryAlertText={noDeliveryAlertText}
                    onClose={() => showNoDeliveryPlate(false)}
                />
            </div>
            {productsSlotSecondStep}
            {isButtonOutSide && submitButton}
        </>
    );
};
